import type {
  DisciplineOrderCalculation,
  DisciplineOrderPayload,
  DisciplineSubscriptionCalculation,
  DisciplineSubscriptionPayload,
} from '../model/interfaces'
import type { AppRequestRoute } from '@shared/config'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useDisciplinePaymentLinksStore } from '../model/payment-links/store'

interface DisciplinePaymentCalculateOptions<TPayload> {
  disciplineId: string | number
  payload: TPayload
}

/** Every selection change recalculates, so only the answer to the latest request may land. */
let activeRequest: AbortController | null = null

export const abortDisciplinePaymentCalculateApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useDisciplinePaymentLinksStore().setCalculating(false)
}

/** Read-only POST: nothing is stored. A refusal (e.g. a total below $1.00) is kept so Generate stays disabled. */
const calculate = async <TResult, TPayload>(
  routeName: AppRequestRoute,
  options: DisciplinePaymentCalculateOptions<TPayload>,
): Promise<TResult | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useDisciplinePaymentLinksStore()
  const url = useAdminRoutes(routeName, { discipline_id: options.disciplineId })

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setCalculating(true)
  store.setCalculationError(null)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload, { signal: request.signal })

    return request.signal.aborted ? null : data.data as TResult
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    const message = getAxiosErrorMessage(err)
    store.setCalculationError(message)
    toast.error(message)

    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setCalculating(false)
    }
  }
}

/** `POST /admin-api/disciplines/{discipline}/orders/calculate` — the one-time price table. */
export const useDisciplineOrderCalculateApi = (
  options: DisciplinePaymentCalculateOptions<Omit<DisciplineOrderPayload, 'expires_at'>>,
): Promise<DisciplineOrderCalculation | null> =>
  calculate<DisciplineOrderCalculation, Omit<DisciplineOrderPayload, 'expires_at'>>('admin.disciplines.api.orders.calculate', options)

/** `POST /admin-api/disciplines/{discipline}/subscriptions/calculate` — the installment price table. */
export const useDisciplineSubscriptionCalculateApi = (
  options: DisciplinePaymentCalculateOptions<Omit<DisciplineSubscriptionPayload, 'expires_at' | 'auto_renew'>>,
): Promise<DisciplineSubscriptionCalculation | null> =>
  calculate<DisciplineSubscriptionCalculation, Omit<DisciplineSubscriptionPayload, 'expires_at' | 'auto_renew'>>(
    'admin.disciplines.api.subscriptions.calculate',
    options,
  )
